import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Table, Spinner, Badge, Row, Card, Container, Button } from "react-bootstrap";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";
import { toWords } from 'number-to-words';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';

const ViewPumpDomesticQuotation = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [quotationData, setQuotationData] = useState(null);

    const fetchQuotationData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}quotation/findById/${id}`, {
                headers: authHeader()
            });
            setQuotationData(response.data);
        } catch (error) {
            console.error("Error fetching quotation data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch quotation data. Please try again.",
            });
            navigate("/quotationIndexTable/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotationData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Draft': return 'secondary';
            case 'Expired': return 'warning';
            case 'Cancelled': return 'danger';
            default: return 'info';
        }
    };

    const getAddressString = (address) => {
        if (!address) return '-';
        return [
            address.customeraddress,
            address.city?.cityname,
            address.state?.statename,
            address.pincode,
            address.country?.countryname
        ].filter(Boolean).join(', ');
    };

    const numberToWordsWithRupees = (num) => {
        if (isNaN(num)) return "Zero Rupees Only";
        const amount = Math.round(num);
        return `${toWords(amount)} Rupees Only`;
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!quotationData) {
        return <div className="alert alert-danger text-center mt-4">Quotation not found</div>;
    }

    // Calculate totals
    const productsTotal = quotationData.quotationItems?.reduce((sum, item) => {
        return sum + (parseFloat(item.totalinr) || 0);
    }, 0) || 0;

    const additionalChargesTotal = quotationData.additionalCharges?.reduce((sum, charge) => {
        return sum + (parseFloat(charge.chargetotal) || 0);
    }, 0) || 0;

    const grossTotal = productsTotal + additionalChargesTotal;
    const roundedGrossTotal = Math.round(grossTotal);

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">SALES QUOTATION - VIEW</h5>
                        {/* <Button
                            // variant="outline-primary"
                            size="sm"
                            onClick={() => navigate(`/CRM/SOQuotation/AraiManufacturingDomesticSalesQuotationPrint/${id}`)}
                        >
                            <FaPrint className="me-1" />
                            PRINT
                        </Button> */}
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-1" />
                            Back
                        </button>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Quotation No</small>
                                <div className="fw-bold">{quotationData.quotationno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Quotation Date</small>
                                <div className="fw-bold">{formatDate(quotationData.quotationdate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Enquiry Ref No</small>
                                <div className="fw-bold">{quotationData.enquiryrefno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge
                                        bg={getStatusBadge(quotationData.status)}
                                        className="px-2 py-1"
                                    >
                                        {quotationData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Quotation Details */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Quotation Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Customer Enquiry No</small>
                                <div className="fw-bold">{quotationData.customerenquirynumber || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Enquiry Ref Date</small>
                                <div className="fw-bold">{formatDate(quotationData.enquiryrefdate)}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Customer Material Code</small>
                                <div className="fw-bold">{quotationData.customermaterialcode || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Quote Validity</small>
                                <div className="fw-bold">{quotationData.quotevalidity || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Delivery Term</small>
                                <div className="fw-bold">{quotationData.deliveryterm || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Payment Term</small>
                                <div className="fw-bold">{quotationData.paymentterm || '-'}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Quotation Type</small>
                                <div>
                                    <Badge bg="info" className="px-2 py-1">
                                        {quotationData.quotationtype || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Quotation Category</small>
                                <div>
                                    <Badge bg="info" className="px-2 py-1">
                                        {quotationData.quotationcategory || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Customer Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Customer Information</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{quotationData.customer?.companyname || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">GST No</small>
                                <div className="fw-bold">{quotationData.customer?.gstno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{quotationData.contactperson || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{quotationData.contactno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{quotationData.email || '-'}</div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mt-2">
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">{getAddressString(quotationData.customeraddress)}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Quotation Items */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Quotation Items</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Type</th>
                                    <th className="fw-bold py-2">Size</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Price</th>
                                    <th className="fw-bold py-2 text-end">Amount</th>
                                    <th className="fw-bold py-2 text-center">GST %</th>
                                    <th className="fw-bold py-2 text-end">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotationData.quotationItems?.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{item.product?.description || '-'}</div>
                                            {item.productremarks && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {item.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{item.partno || '-'}</td>
                                        <td>{item.hsnsacno || '-'}</td>
                                        <td>{item.serialnumber || '-'}</td>
                                        <td>{item.product?.moc || '-'}</td>
                                        <td>{item.product?.type || '-'}</td>
                                        <td>{item.product?.size || '-'}</td>
                                        <td className="text-center">{item.quantity}</td>
                                        <td className="text-center">{item.uom || '-'}</td>
                                        <td className="text-end">{formatCurrency(item.unitrate)}</td>
                                        <td className="text-end">{formatCurrency(item.amount)}</td>
                                        <td className="text-center">{item.gst}%</td>
                                        <td className="text-end fw-bold">{formatCurrency(item.totalinr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <td colSpan="13" className="text-end fw-bold py-2">
                                        Products Total:
                                    </td>
                                    <td className="text-end fw-bold py-2">
                                        {formatCurrency(productsTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Additional Charges */}
            {quotationData.additionalCharges?.length > 0 && (
                <Card className="shadow-sm border-0 rounded mb-3">
                    <Card.Header className="bg-light py-2">
                        <h6 className="mb-0 fw-bold">Additional Charges</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table bordered size="sm" className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="fw-bold py-2">#</th>
                                        <th className="fw-bold py-2">Charge Type</th>
                                        <th className="fw-bold py-2 text-end">Amount</th>
                                        <th className="fw-bold py-2 text-center">GST %</th>
                                        <th className="fw-bold py-2 text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quotationData.additionalCharges.map((charge, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{charge.chargetype}</td>
                                            <td className="text-end">{formatCurrency(charge.chargeamount)}</td>
                                            <td className="text-center">{charge.chargegst}%</td>
                                            <td className="text-end fw-bold">{formatCurrency(charge.chargetotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td colSpan="4" className="text-end fw-bold py-2">
                                            Charges Total:
                                        </td>
                                        <td className="text-end fw-bold py-2">
                                            {formatCurrency(additionalChargesTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Summary Section */}
            <Row>
                <Col lg={6} className="ms-auto">
                    <Card className="shadow-sm border-0 rounded">
                        <Card.Header className="bg-light py-2">
                            <h6 className="mb-0 fw-bold text-center">Quotation Summary</h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="space-y-2">
                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Products Total:</span>
                                    <span className="fw-bold">{formatCurrency(productsTotal)}</span>
                                </div>

                                {quotationData.additionalCharges?.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                        <span className="fw-semibold">Additional Charges:</span>
                                        <span className="fw-bold">{formatCurrency(additionalChargesTotal)}</span>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Gross Total:</span>
                                    <span className="fw-bold">{formatCurrency(grossTotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                    <span className="fw-bold">Grand Total (Rounded)</span>
                                    <span className="fw-bold text-primary">{formatCurrency(roundedGrossTotal)}</span>
                                </div>

                                <div className="mt-3 pt-2 border-top">
                                    <div className="text-center">
                                        <small className="text-muted fw-bold">Amount in Words</small>
                                        <div className="fw-semibold text-dark small">
                                            {numberToWordsWithRupees(roundedGrossTotal)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </button>
            </div>
        </Container>
    );
};

export default ViewPumpDomesticQuotation;